// ABOUTME: React hook for managing LemonSqueezy checkout overlay lifecycle and user interactions
// ABOUTME: Handles API calls to create checkout sessions, opens overlays, and manages success/error states

import { useState, useCallback } from 'react';
import { subscriptionAPI } from '../services/api';
import { openCheckoutOverlay, isLemonSqueezyLoaded } from '../utils/lemonsqueezy';

/**
 * Options for useCheckoutOverlay hook
 */
export interface UseCheckoutOverlayOptions {
  /**
   * Callback fired when checkout completes successfully
   */
  onSuccess?: () => void;

  /**
   * Callback fired when user cancels/closes checkout without completing
   */
  onCancel?: () => void;

  /**
   * Callback fired when an error occurs
   */
  onError?: (error: Error) => void;

  /**
   * Family ID to associate with the subscription
   */
  familyId: number;

  /**
   * User email to pre-fill in checkout
   */
  email: string;
}

/**
 * Return value from useCheckoutOverlay hook
 */
export interface UseCheckoutOverlayReturn {
  /**
   * Function to open the checkout overlay
   */
  openCheckout: () => Promise<void>;

  /**
   * Whether checkout is currently loading/processing
   */
  isLoading: boolean;

  /**
   * Error message if checkout fails
   */
  error: string | null;
}

/**
 * Hook for managing LemonSqueezy checkout overlay
 * Issue #1030
 *
 * @example
 * ```tsx
 * const { openCheckout, isLoading, error } = useCheckoutOverlay({
 *   familyId: family.id,
 *   email: user.email,
 *   onSuccess: () => {
 *     console.log('Payment successful!');
 *     refreshSubscriptionStatus();
 *   }
 * });
 *
 * <Button onClick={openCheckout} disabled={isLoading}>
 *   {isLoading ? 'Loading...' : 'Subscribe Now'}
 * </Button>
 * ```
 */
export function useCheckoutOverlay(options: UseCheckoutOverlayOptions): UseCheckoutOverlayReturn {
  const { onSuccess, onCancel, onError, familyId, email } = options;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openCheckout = useCallback(async () => {
    // Validate LemonSqueezy SDK is loaded
    if (!isLemonSqueezyLoaded()) {
      const err = new Error('LemonSqueezy SDK is not loaded. Please refresh the page and try again.');
      setError(err.message);
      onError?.(err);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Call API to create checkout session
      const response = await subscriptionAPI.createCheckout(familyId, email);

      // Open the checkout overlay with the returned URL
      openCheckoutOverlay(response.url, (event: string) => {
        // Handle overlay close events
        setIsLoading(false);

        if (event === 'Checkout.Success') {
          // Payment completed successfully
          onSuccess?.();
        } else if (event === 'Checkout.Closed') {
          // User closed without completing payment
          onCancel?.();
        }
      });

    } catch (err: unknown) {
      // Handle API errors
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosErr.response?.data?.error || axiosErr.message || 'Failed to start checkout. Please try again.';
      setError(errorMessage);
      setIsLoading(false);

      const error = new Error(errorMessage);
      onError?.(error);

      console.error('Checkout error:', err);
    }
  }, [familyId, email, onSuccess, onCancel, onError]);

  return {
    openCheckout,
    isLoading,
    error
  };
}
